import * as routes from 'api';

import {
  createAsyncGetAction,
  createAsyncPostAction,
  createAsyncPutAction,
  withAlerts,
} from 'util/redux';

export const actionTypes = {
  FETCH_STATUS_REQUEST: 'USER_WEB_TIMECLOCK/FETCH_STATUS_REQUEST',
  FETCH_STATUS_SUCCESS: 'USER_WEB_TIMECLOCK/FETCH_STATUS_SUCCESS',
  FETCH_STATUS_FAILURE: 'USER_WEB_TIMECLOCK/FETCH_STATUS_FAILURE',
  CLOCK_IN_REQUEST: 'USER_WEB_TIMECLOCK/CLOCK_IN_REQUEST',
  CLOCK_IN_SUCCESS: 'USER_WEB_TIMECLOCK/CLOCK_IN_SUCCESS',
  CLOCK_IN_FAILURE: 'USER_WEB_TIMECLOCK/CLOCK_IN_FAILURE',
  CLOCK_IN_UNSCHEDULED_REQUEST:
    'USER_WEB_TIMECLOCK/CLOCK_IN_UNSCHEDULED_REQUEST',
  CLOCK_IN_UNSCHEDULED_SUCCESS:
    'USER_WEB_TIMECLOCK/CLOCK_IN_UNSCHEDULED_SUCCESS',
  CLOCK_IN_UNSCHEDULED_FAILURE:
    'USER_WEB_TIMECLOCK/CLOCK_IN_UNSCHEDULED_FAILURE',
  CLOCK_OUT_REQUEST: 'USER_WEB_TIMECLOCK/CLOCK_OUT_REQUEST',
  CLOCK_OUT_SUCCESS: 'USER_WEB_TIMECLOCK/CLOCK_OUT_SUCCESS',
  CLOCK_OUT_FAILURE: 'USER_WEB_TIMECLOCK/CLOCK_OUT_FAILURE',
  CLOCK_IN_BREAK_REQUEST: 'USER_WEB_TIMECLOCK/CLOCK_IN_BREAK_REQUEST',
  CLOCK_IN_BREAK_SUCCESS: 'USER_WEB_TIMECLOCK/CLOCK_IN_BREAK_SUCCESS',
  CLOCK_IN_BREAK_FAILURE: 'USER_WEB_TIMECLOCK/CLOCK_IN_BREAK_FAILURE',
  CLOCK_OUT_BREAK_REQUEST: 'USER_WEB_TIMECLOCK/CLOCK_OUT_BREAK_REQUEST',
  CLOCK_OUT_BREAK_SUCCESS: 'USER_WEB_TIMECLOCK/CLOCK_OUT_BREAK_SUCCESS',
  CLOCK_OUT_BREAK_FAILURE: 'USER_WEB_TIMECLOCK/CLOCK_OUT_BREAK_FAILURE',
};

/**
 * @typedef {Object} TimeclockStatusPayload
 * @property {Object} job - Job information with user details
 * @property {string} job.user_first_name - User's first name
 * @property {Array} shifts - Array of shift objects
 * @property {Array} mandated_breaks - Array of break objects
 * @property {string|null} clocked_in_at - Clock in timestamp
 * @property {string|null} clocked_in_break_at - Break clock in timestamp
 * @property {string} type - Timeclock state type
 */

/**
 * @typedef {Object} ApiResponse
 * @property {TimeclockStatusPayload} payload - Response payload
 */

/**
 * @param {function(ApiResponse): void} onSuccess - Success callback
 */
export const fetchStatus = onSuccess =>
  withAlerts(
    createAsyncGetAction(routes.userWebTimeclockStatusRoute(), [
      actionTypes.FETCH_STATUS_REQUEST,
      actionTypes.FETCH_STATUS_SUCCESS,
      actionTypes.FETCH_STATUS_FAILURE,
    ]),
    {
      onSuccess,
    }
  );

/**
 * @typedef {Object} ClockInPayload
 * @property {string} clocked_in_at - Clock in timestamp
 */

/**
 * @typedef {Object} ClockInResponse
 * @property {ClockInPayload} payload - Response payload
 */

/**
 * @param {string} shiftId - The shift ID to clock into
 * @param {function(ClockInResponse): void} onSuccess - Success callback
 */
export const clockIn = (shiftId, onSuccess) => dispatch =>
  dispatch(
    withAlerts(
      createAsyncPutAction(routes.userWebTimeclockClockInRoute(shiftId), [
        actionTypes.CLOCK_IN_REQUEST,
        actionTypes.CLOCK_IN_SUCCESS,
        actionTypes.CLOCK_IN_FAILURE,
      ]),
      {
        onSuccess: async response => onSuccess(response),
      }
    )
  );

/**
 * @param {function(ClockInResponse): void} onSuccess - Success callback
 */
export const clockInUnscheduled = onSuccess => dispatch =>
  dispatch(
    withAlerts(
      createAsyncPostAction(routes.userWebTimeclockClockInUnscheduleRoute(), [
        actionTypes.CLOCK_IN_UNSCHEDULED_REQUEST,
        actionTypes.CLOCK_IN_UNSCHEDULED_SUCCESS,
        actionTypes.CLOCK_IN_UNSCHEDULED_FAILURE,
      ]),
      {
        onSuccess: async response => onSuccess(response),
      }
    )
  );

/**
 * @param {string} shiftId - The shift ID to clock out of
 * @param {function(): void} onSuccess - Success callback
 */
export const clockOut = (shiftId, onSuccess) =>
  withAlerts(
    createAsyncPutAction(routes.userWebTimeclockClockOutRoute(shiftId), [
      actionTypes.CLOCK_OUT_REQUEST,
      actionTypes.CLOCK_OUT_SUCCESS,
      actionTypes.CLOCK_OUT_FAILURE,
    ]),
    {
      onSuccess,
    }
  );

/**
 * @typedef {Object} ClockInBreakPayload
 * @property {string} back_at - Time when break should end
 */

/**
 * @typedef {Object} ClockInBreakResponse
 * @property {ClockInBreakPayload} payload - Response payload
 */

/**
 * @param {string} shiftId - The shift ID
 * @param {string} breakId - The break ID to clock into
 * @param {function(ClockInBreakResponse): void} onSuccess - Success callback
 */
export const clockInBreak = (shiftId, breakId, onSuccess) =>
  withAlerts(
    createAsyncPutAction(
      routes.userWebTimeclockClockInBreakRoute(shiftId, breakId),
      [
        actionTypes.CLOCK_IN_BREAK_REQUEST,
        actionTypes.CLOCK_IN_BREAK_SUCCESS,
        actionTypes.CLOCK_IN_BREAK_FAILURE,
      ]
    ),
    {
      onSuccess,
    }
  );

/**
 * @param {string} shiftId - The shift ID to clock out break from
 * @param {function(): void} onSuccess - Success callback
 */
export const clockOutBreak = (shiftId, onSuccess) =>
  withAlerts(
    createAsyncPutAction(routes.userWebTimeclockClockOutBreakRoute(shiftId), [
      actionTypes.CLOCK_OUT_BREAK_REQUEST,
      actionTypes.CLOCK_OUT_BREAK_SUCCESS,
      actionTypes.CLOCK_OUT_BREAK_FAILURE,
    ]),
    {
      onSuccess,
    }
  );
